import { Button } from "components/DesignSystem/Button/Button";
import { motion } from "framer-motion";
import { useModal } from "hooks/useModal";
import { useState } from "react";
import { BulkActionModal } from "./BulkActionModal";
import { BulkTagAssign } from "./BulkTagAssign";

export const OPS_STAGE = "ops_stage";
export const TAGS = "tags";
export const SALES_PERSON = "sales_person";
export const AGENT = "agent";

export type OpsBulkActionType =
  | typeof OPS_STAGE
  | typeof TAGS
  | typeof SALES_PERSON
  | typeof AGENT;

export type OpsBulkActionConfig = {
  title: string;
  label: string;
  placeholder: string;
  toastTitle: string;
  toastMessage: string;
  loadingMessage: string;
};

type OpsBulkActionProps = {
  selectedRows: string[];
  resetSelection: () => void;
};

const ACTION_CONFIG: Record<OpsBulkActionType, OpsBulkActionConfig> = {
  sales_person: {
    title: "Update Sales Person",
    label: "Select sales person",
    placeholder: "Select",
    loadingMessage: "Updating sales person...",
    toastTitle: "Sales person updated",
    toastMessage: "Sales person updated for",
  },
  agent: {
    title: "Update Agent",
    label: "Select agent",
    placeholder: "Select",
    loadingMessage: "Updating agent...",
    toastTitle: "Agent updated",
    toastMessage: "Agent updated for",
  },
  ops_stage: {
    title: "Update Ops Status",
    label: "Select ops status",
    placeholder: "Select",
    loadingMessage: "Updating ops status...",
    toastTitle: "Ops status updated",
    toastMessage: "Ops status updated for",
  },
  tags: {
    title: "Update Tags",
    label: "Select tag(s)",
    placeholder: "Select",
    loadingMessage: "Updating tags...",
    toastTitle: "Tags updated",
    toastMessage: "Tags updated for",
  },
};

export const OpsBulkAction = ({
  selectedRows,
  resetSelection,
}: OpsBulkActionProps) => {
  const bulkAction = useModal();
  const [bulkActionType, setBulkActionType] =
    useState<OpsBulkActionType | null>(null);

  const openModal = (actionType: OpsBulkActionType) => {
    setBulkActionType(actionType);
    bulkAction.open();
  };

  return (
    <>
      <motion.span
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ease: "easeInOut", duration: 0.2 }}
        key={selectedRows.toString()}
        className="t-flex t-gap-3 t-items-center"
      >
        <div className="t-text-subtext t-text-text-30">Update:</div>
        <Button size="small" onClick={() => openModal("sales_person")}>
          Sales Person
        </Button>
        <Button size="small" onClick={() => openModal("agent")}>
          Agent
        </Button>
        <Button size="small" onClick={() => openModal("ops_stage")}>
          Ops Status
        </Button>
        <Button size="small" onClick={() => openModal("tags")}>
          Tags
        </Button>
      </motion.span>

      {bulkAction.isOpen && bulkActionType !== TAGS && (
        <BulkActionModal
          {...bulkAction}
          selectedRows={selectedRows}
          actionType={bulkActionType}
          actionDetails={bulkActionType ? ACTION_CONFIG[bulkActionType] : null}
          resetSelection={resetSelection}
          setBulkActionType={setBulkActionType}
        />
      )}

      {bulkAction.isOpen && bulkActionType === TAGS && (
        <BulkTagAssign
          {...bulkAction}
          selectedRows={selectedRows}
          actionType={bulkActionType}
          resetSelection={resetSelection}
          actionDetails={ACTION_CONFIG[bulkActionType]}
        />
      )}
    </>
  );
};
